import AttachmentRoundedIcon from '@mui/icons-material/AttachmentRounded';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import type {
  AssistantConsultantDeadlineOutcome,
  AssistantConsultantDeadlinesDraft,
  AssistantDeadlineAiField,
  AssistantDeadlineSkipReason,
  CreateConsultantDeadlinesAction,
} from '@prospero/shared/src/assistant/assistant-contract';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { RoutesList } from '../../config/routes-list';
import { TOUCH_TARGET_PX } from '../../config/touch-target';
import { ConsultantDeadlineCreateDialog } from '../../pages/consultant/deadlines/components/consultant-deadline-create-dialog';
import { buildDeadlineCalendarLink } from '../../pages/consultant/deadlines/deadline-calendar-link';
import { formatIsoDay } from '../../pages/consultant/deadlines/deadline-row-format';
import {
  DEFAULT_CONSULTANT_DEADLINES_FILTERS,
  serializeConsultantDeadlinesFilters,
} from '../../pages/consultant/deadlines/deadline-url-filters';
import {
  type AssistantDraftDialogValues,
  toAssistantDraftDialogValues,
} from '../../pages/consultant/deadlines/mappers/assistant-draft-dialog.mapper';
import { AiActDisclaimer } from '../ai-act/ai-act-disclaimer';
import { AiProposedLabel } from '../ai-act/ai-proposed-label';
import { ConsultantAssistantBlockedAlert } from './consultant-assistant-blocked-alert';
import {
  type ConfirmFailureKind,
  classifyConfirmFailure,
} from './consultant-deadlines-confirm-failure';
import {
  useConfirmConsultantDeadlines,
  useReloadAssistantThreads,
} from './use-confirm-consultant-deadlines';

const KEY = 'guida.assistant.consultantDeadlines';
const VISIBLE_CLIENTS = 5;

type Translate = ReturnType<typeof useTranslation>['t'];
type ConfirmFailure = Exclude<ConfirmFailureKind, 'reload'> | null;

const SKIP_REASON_FALLBACKS: Record<AssistantDeadlineSkipReason, string> = {
  subscription_blocked: "Saltata: l'abbonamento del cliente non è attivo",
  out_of_perimeter: 'Saltata: non segui più questo cliente',
  failed: 'Non creata: errore imprevisto, riprova',
  not_created: 'Non creata',
};

function DraftRow({
  label,
  value,
  aiProposed = false,
}: {
  label: string;
  value: ReactNode;
  aiProposed?: boolean;
}) {
  return (
    <Stack spacing={0.25}>
      <Typography color="text.secondary" variant="caption">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
      {aiProposed ? <AiProposedLabel /> : null}
    </Stack>
  );
}

function ClientList({ clients }: { clients: AssistantConsultantDeadlinesDraft['clients'] }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const visibleClients = expanded ? clients : clients.slice(0, VISIBLE_CLIENTS);
  const hiddenCount = clients.length - visibleClients.length;
  return (
    <Stack spacing={0.25}>
      <Typography color="text.secondary" variant="caption">
        {t(`${KEY}.clients`, { defaultValue: 'Clienti' })}
      </Typography>
      {visibleClients.map((client) => (
        <Typography key={client.tenantId} variant="body2">
          {client.name}
        </Typography>
      ))}
      {hiddenCount > 0 || expanded ? (
        <Button
          onClick={() => setExpanded((current) => !current)}
          size="small"
          sx={{ alignSelf: 'flex-start', minHeight: TOUCH_TARGET_PX }}
        >
          {expanded
            ? t(`${KEY}.showFewerClients`, { defaultValue: 'Mostra meno' })
            : t(`${KEY}.moreClients`, {
                count: hiddenCount,
                defaultValue_one: 'e un altro',
                defaultValue: 'e altri {{count}}',
              })}
        </Button>
      ) : null}
    </Stack>
  );
}

function DraftDates({
  draft,
  aiFields,
}: {
  draft: AssistantConsultantDeadlinesDraft;
  aiFields: ReadonlySet<AssistantDeadlineAiField>;
}) {
  const { t, i18n } = useTranslation();
  return (
    <>
      {draft.issuedAt ? (
        <DraftRow
          aiProposed={aiFields.has('issuedAt')}
          label={t(`${KEY}.issuedAt`, { defaultValue: 'Data del documento' })}
          value={formatIsoDay(draft.issuedAt, i18n.language)}
        />
      ) : null}
      <DraftRow
        aiProposed={aiFields.has('dueAt')}
        label={t(`${KEY}.dueAt`, { defaultValue: 'Data di scadenza' })}
        value={formatIsoDay(draft.dueAt, i18n.language)}
      />
      {draft.validityMonths !== null ? (
        <DraftRow
          aiProposed={aiFields.has('validityMonths')}
          label={t(`${KEY}.validity`, { defaultValue: 'Validità' })}
          value={t(`${KEY}.renewsEvery`, {
            count: draft.validityMonths,
            defaultValue_one: 'Si rinnova ogni mese',
            defaultValue: 'Si rinnova ogni {{count}} mesi',
          })}
        />
      ) : null}
    </>
  );
}

function DraftWarnings({ draft }: { draft: AssistantConsultantDeadlinesDraft }) {
  const { t } = useTranslation();
  return (
    <>
      <DraftRow
        label={t(`${KEY}.warningDaysLabel`, { defaultValue: 'Preavvisi' })}
        value={
          draft.warningDays.length > 0
            ? t(`${KEY}.warningDaysValue`, {
                days: draft.warningDays.join(', '),
                defaultValue: '{{days}} giorni prima',
              })
            : t(`${KEY}.noWarningDays`, { defaultValue: 'Nessun preavviso' })
        }
      />
      {draft.droppedWarningDays.length > 0 ? (
        <Alert severity="warning">
          {t(`${KEY}.droppedWarningDays`, {
            days: draft.droppedWarningDays.join(', '),
            defaultValue: 'Tolti perché cadrebbero prima di oggi: {{days}} giorni',
          })}
        </Alert>
      ) : null}
    </>
  );
}

function DraftDocument({ document }: { document: AssistantConsultantDeadlinesDraft['document'] }) {
  const { t } = useTranslation();
  if (!document) {
    return null;
  }
  return (
    <>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
        <AttachmentRoundedIcon color="action" fontSize="small" />
        <Typography color="text.secondary" sx={{ overflowWrap: 'anywhere' }} variant="caption">
          {t(`${KEY}.document`, {
            filename: document.filename,
            defaultValue: 'Documento: {{filename}}',
          })}
        </Typography>
      </Box>
      {document.aiFields.length > 0 ? <AiActDisclaimer /> : null}
    </>
  );
}

function DraftSummary({ draft }: { draft: AssistantConsultantDeadlinesDraft }) {
  const { t } = useTranslation();
  const aiFields = new Set<AssistantDeadlineAiField>(draft.document?.aiFields ?? []);
  return (
    <Stack spacing={1}>
      <ClientList clients={draft.clients} />
      <DraftRow
        aiProposed={aiFields.has('documentType')}
        label={
          draft.title
            ? t(`${KEY}.freeName`, { defaultValue: 'Nome del documento' })
            : t(`${KEY}.documentType`, { defaultValue: 'Tipo di documento' })
        }
        value={draft.title ?? draft.documentTypeName}
      />
      <DraftDates aiFields={aiFields} draft={draft} />
      <DraftWarnings draft={draft} />
      <DraftRow
        label={t(`${KEY}.uploader`, { defaultValue: 'Chi carica il documento' })}
        value={
          draft.clientCanUpload
            ? t(`${KEY}.clientCanUpload`, { defaultValue: 'Lo carica il cliente' })
            : t(`${KEY}.clientCannotUpload`, { defaultValue: 'Lo carichi tu' })
        }
      />
      <DraftDocument document={draft.document} />
    </Stack>
  );
}

function outcomeText(outcome: AssistantConsultantDeadlineOutcome, t: Translate): string {
  if (outcome.status === 'created') {
    return t(`${KEY}.outcome.created`, { defaultValue: 'Creata' });
  }
  return t(`${KEY}.outcome.${outcome.reason}`, {
    defaultValue: SKIP_REASON_FALLBACKS[outcome.reason],
  });
}

function buildClientListLink(tenantId: string): string {
  const params = serializeConsultantDeadlinesFilters({
    ...DEFAULT_CONSULTANT_DEADLINES_FILTERS,
    tenantId,
  });
  return `${RoutesList.CONSULTANT_DEADLINES}?${params.toString()}`;
}

function OutcomeRow({
  outcome,
  draftDueAt,
}: {
  outcome: AssistantConsultantDeadlineOutcome;
  draftDueAt: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isCreated = outcome.status === 'created';
  return (
    <Stack data-testid={`consultant-deadlines-outcome-${outcome.tenantId}`} spacing={0.5}>
      <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        {isCreated ? (
          <CheckCircleOutlinedIcon color="success" fontSize="small" />
        ) : (
          <ReportProblemOutlinedIcon color="warning" fontSize="small" />
        )}
        <Typography sx={{ fontWeight: 600 }} variant="body2">
          {outcome.tenantName}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {outcomeText(outcome, t)}
        </Typography>
      </Stack>
      {outcome.status === 'created' ? (
        <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button
            data-testid={`consultant-deadlines-view-in-calendar-${outcome.deadlineId}`}
            onClick={() =>
              navigate(
                buildDeadlineCalendarLink({
                  deadlineId: outcome.deadlineId,
                  dueAt: outcome.dueAt ?? draftDueAt,
                }),
              )
            }
            startIcon={<EventOutlinedIcon />}
            sx={{ minHeight: TOUCH_TARGET_PX }}
            variant="outlined"
          >
            {t(`${KEY}.viewInCalendar`, { defaultValue: 'Vedi nel calendario' })}
          </Button>
          <Button
            data-testid={`consultant-deadlines-open-in-list-${outcome.deadlineId}`}
            onClick={() => navigate(buildClientListLink(outcome.tenantId))}
            startIcon={<ListAltOutlinedIcon />}
            sx={{ minHeight: TOUCH_TARGET_PX }}
            variant="text"
          >
            {t(`${KEY}.openInClientList`, { defaultValue: "Apri nell'elenco del cliente" })}
          </Button>
        </Stack>
      ) : null}
      {outcome.status === 'skipped' && outcome.reason === 'subscription_blocked' ? (
        <Button
          data-testid={`consultant-deadlines-open-client-${outcome.tenantId}`}
          onClick={() => navigate(`${RoutesList.CONSULTANT_CLIENT_STATS}/${outcome.tenantId}`)}
          startIcon={<StorefrontOutlinedIcon />}
          sx={{ alignSelf: 'flex-start', minHeight: TOUCH_TARGET_PX }}
          variant="text"
        >
          {t(`${KEY}.openClientPage`, { defaultValue: 'Apri la scheda del cliente' })}
        </Button>
      ) : null}
    </Stack>
  );
}

function confirmedTitle(outcomes: AssistantConsultantDeadlineOutcome[], t: Translate): string {
  const createdCount = outcomes.filter((outcome) => outcome.status === 'created').length;
  return createdCount === 0
    ? t(`${KEY}.noneCreatedTitle`, { defaultValue: 'Nessuna scadenza creata' })
    : t(`${KEY}.createdTitle`, {
        count: createdCount,
        defaultValue_one: '{{count}} scadenza creata',
        defaultValue: '{{count}} scadenze create',
      });
}

/** Proposal actions: "Crea", or "Modifica e crea" for those who prefer to correct by hand. */
function ProposalActions({
  isPending,
  isReloading,
  onCreate,
  onEditInDialog,
}: {
  isPending: boolean;
  /** The dialog or another request just touched the card: the reloaded thread says whether it is still open. */
  isReloading: boolean;
  onCreate: () => void;
  onEditInDialog: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
      <Button
        disabled={isPending || isReloading}
        onClick={onCreate}
        startIcon={isPending ? <CircularProgress color="inherit" size={16} /> : undefined}
        sx={{ minHeight: TOUCH_TARGET_PX }}
        variant="contained"
      >
        {isPending
          ? t(`${KEY}.creating`, { defaultValue: 'Creazione…' })
          : t(`${KEY}.createCta`, { defaultValue: 'Crea' })}
      </Button>
      <Button
        disabled={isPending || isReloading}
        onClick={onEditInDialog}
        startIcon={<EditOutlinedIcon />}
        sx={{ minHeight: TOUCH_TARGET_PX }}
        variant="outlined"
      >
        {t(`${KEY}.editInDialog`, { defaultValue: 'Modifica e crea' })}
      </Button>
    </Stack>
  );
}

function ConfirmFailureAlert({
  failure,
  onRetry,
}: {
  failure: Exclude<ConfirmFailure, null>;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  if (failure === 'tokenExpired') {
    return (
      <Alert severity="warning">
        {t(`${KEY}.tokenExpired`, {
          defaultValue: 'Il documento non è più disponibile: trascinalo di nuovo nella chat.',
        })}
      </Alert>
    );
  }
  if (failure === 'noWorkspace' || failure === 'aiDisabled') {
    return <ConsultantAssistantBlockedAlert reason={failure} />;
  }
  if (failure === 'actionNotFound') {
    return (
      <Alert severity="warning">
        {t(`${KEY}.actionNotFound`, {
          defaultValue: "Questa scheda non è più disponibile: chiedi di nuovo all'assistente.",
        })}
      </Alert>
    );
  }
  return (
    <Alert
      action={
        <Button color="inherit" onClick={onRetry} size="small" sx={{ minHeight: TOUCH_TARGET_PX }}>
          {t('guida.assistant.retry')}
        </Button>
      }
      severity="error"
    >
      {t(`${KEY}.error`, { defaultValue: 'Non è stato possibile creare le scadenze.' })}
    </Alert>
  );
}

function hasFailedOutcome(outcomes: AssistantConsultantDeadlineOutcome[]): boolean {
  return outcomes.some((outcome) => outcome.status === 'skipped' && outcome.reason === 'failed');
}

/** Same confirm route: the server re-attempts only the clients still without a deadline. */
function RetryFailedAction({ isPending, onRetry }: { isPending: boolean; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <Button
      disabled={isPending}
      onClick={onRetry}
      startIcon={isPending ? <CircularProgress color="inherit" size={16} /> : undefined}
      sx={{ alignSelf: 'flex-start', minHeight: TOUCH_TARGET_PX }}
      variant="outlined"
    >
      {t(`${KEY}.retryFailed`, { defaultValue: 'Riprova per i clienti non creati' })}
    </Button>
  );
}

/** One invitation at a time: the failure alert, else the proposal actions or the failed-clients retry. */
function CardFooter({
  outcomes,
  failure,
  isPending,
  isReloading,
  onConfirm,
  onEditInDialog,
}: {
  outcomes: AssistantConsultantDeadlineOutcome[] | null;
  failure: ConfirmFailure;
  isPending: boolean;
  isReloading: boolean;
  onConfirm: () => void;
  onEditInDialog: () => void;
}) {
  if (failure) {
    return <ConfirmFailureAlert failure={failure} onRetry={onConfirm} />;
  }
  if (!outcomes) {
    return (
      <ProposalActions
        isPending={isPending}
        isReloading={isReloading}
        onCreate={onConfirm}
        onEditInDialog={onEditInDialog}
      />
    );
  }
  return hasFailedOutcome(outcomes) ? (
    <RetryFailedAction isPending={isPending} onRetry={onConfirm} />
  ) : null;
}

/**
 * The draft card: nothing is created until "Crea" (or a save of "Modifica e crea").
 * The confirmed state comes from `action.outcomes` (saved by the confirm or by the
 * dialog, written into the cached thread), so a reopened panel never invites a second
 * press, even when every client was skipped. Outcomes of a confirm made from this card
 * win: they are newer than the prop.
 */
export function ConsultantDeadlinesDraftCard({
  action,
}: {
  action: CreateConsultantDeadlinesAction;
}) {
  const { t } = useTranslation();
  const confirm = useConfirmConsultantDeadlines();
  const reloadThreads = useReloadAssistantThreads();
  const [confirmedOutcomes, setConfirmedOutcomes] = useState<
    AssistantConsultantDeadlineOutcome[] | null
  >(null);
  const [failure, setFailure] = useState<ConfirmFailure>(null);
  // Taken when the dialog opens: a thread refetch while it is open must not reset what was typed.
  const [dialogDraft, setDialogDraft] = useState<AssistantDraftDialogValues | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  // Between a save made elsewhere and the reloaded thread the buttons would invite a second save.
  const [isReloadingThread, setIsReloadingThread] = useState(false);
  const outcomes = confirmedOutcomes ?? action.outcomes ?? null;

  const openDialog = () => {
    setDialogDraft(toAssistantDraftDialogValues(action));
    setIsDialogOpen(true);
  };

  /** The thread brings back the card state saved by another request (dialog, other tab). */
  const reloadCardState = async () => {
    setIsReloadingThread(true);
    try {
      await reloadThreads();
    } finally {
      setIsReloadingThread(false);
    }
  };

  /** Saved, cancelled or refused (card closed meanwhile). */
  const handleDialogClosed = async () => {
    setIsDialogOpen(false);
    await reloadCardState();
  };

  const handleCreate = async () => {
    setFailure(null);
    try {
      const response = await confirm.mutateAsync(action.id);
      setConfirmedOutcomes(response.outcomes);
    } catch (error) {
      const failureKind = classifyConfirmFailure(error);
      if (failureKind === 'reload') {
        await reloadCardState();
        return;
      }
      setFailure(failureKind);
    }
  };

  return (
    <Paper
      aria-label={t(`${KEY}.ariaLabel`, { defaultValue: 'Bozza di nuove scadenze' })}
      data-testid={`consultant-deadlines-draft-card-${action.id}`}
      role="region"
      sx={{ borderRadius: 2, p: 2 }}
      variant="outlined"
    >
      <Stack spacing={1.5}>
        <Typography sx={{ fontWeight: 700 }} variant="body2">
          {outcomes
            ? confirmedTitle(outcomes, t)
            : t(`${KEY}.title`, { defaultValue: 'Nuove scadenze' })}
        </Typography>
        {outcomes ? (
          <Stack spacing={1.5}>
            {outcomes.map((outcome) => (
              <OutcomeRow
                draftDueAt={action.draft.dueAt}
                key={outcome.tenantId}
                outcome={outcome}
              />
            ))}
          </Stack>
        ) : (
          <DraftSummary draft={action.draft} />
        )}
        <CardFooter
          failure={failure}
          isPending={confirm.isPending}
          isReloading={isReloadingThread}
          onConfirm={handleCreate}
          onEditInDialog={openDialog}
          outcomes={outcomes}
        />
      </Stack>
      {dialogDraft ? (
        <ConsultantDeadlineCreateDialog
          initialDraft={dialogDraft}
          onClose={handleDialogClosed}
          onCreated={() => setIsDialogOpen(false)}
          open={isDialogOpen}
        />
      ) : null}
    </Paper>
  );
}
